import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Bell, Check, CheckCheck } from "lucide-react";
import { Button } from "./ui/button";
import { formatDistanceToNow } from "date-fns";

const NotificationBell = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const dropdownPortalRef = useRef(null);
  const token = localStorage.getItem("token");

  // Fetch unread count on mount and periodically
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // Poll every 30s
    return () => clearInterval(interval);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside both the bell button and the dropdown portal
      const isOutsideBell = dropdownRef.current && !dropdownRef.current.contains(event.target);
      const isOutsideDropdown = dropdownPortalRef.current && !dropdownPortalRef.current.contains(event.target);
      
      if (isOutsideBell && isOutsideDropdown) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread-count`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error("Failed to fetch unread count:", error);
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/notifications?limit=15`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setNotifications(response.data);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBellClick = () => {
    if (!isOpen) {
      fetchNotifications();
    }
    setIsOpen(!isOpen);
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read
    if (!notification.read) {
      try {
        await axios.put(`${API}/notifications/mark-read`, 
          { notification_ids: [notification.notification_id] },
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
        setNotifications(prev => 
          prev.map(n => n.notification_id === notification.notification_id 
            ? { ...n, read: true } 
            : n
          )
        );
      } catch (error) {
        console.error("Failed to mark notification as read:", error);
      }
    }

    // System notifications carry their own link; mentions open the lead's All Activity tab
    setIsOpen(false);
    navigate(notification.link || `/leads?open=${notification.lead_id}&tab=all`);
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/mark-all-read`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  const formatTime = (dateStr) => {
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
    } catch {
      return dateStr;
    }
  };

  const getActivityLabel = (type) => {
    const labels = {
      comment: "commented on",
      call: "logged a call on",
      text: "logged a text on",
      meeting: "added a meeting to",
      scheduled_call: "scheduled a call on",
      stuck_send: "paused a stuck send in",
      send_failed: "cancelled failed sends in"
    };
    return labels[type] || "mentioned you on";
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={handleBellClick}
        className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className={`w-5 h-5 ${unreadCount > 0 ? 'text-indigo-600' : 'text-slate-500'}`} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown - using Portal to render at document root for proper z-index stacking */}
      {isOpen && createPortal(
        <div 
          ref={dropdownPortalRef}
          className="fixed w-80 bg-white rounded-lg shadow-xl border border-slate-200 overflow-hidden"
          style={{ 
            zIndex: 999999,
            top: '70px',
            left: '16px'
          }}
        >  {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50">
            <h3 className="font-semibold text-slate-800">Notifications</h3>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleMarkAllRead}
                className="text-xs text-indigo-600 hover:text-indigo-700 h-auto py-1"
              >
                <CheckCheck className="w-3 h-3 mr-1" />
                Mark all read
              </Button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-slate-500">
                <div className="w-5 h-5 border-2 border-slate-200 border-t-indigo-600 rounded-full animate-spin mx-auto"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-6 text-center text-slate-500">
                <Bell className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">No notifications yet</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <button
                  key={notification.notification_id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                    !notification.read ? 'bg-indigo-50/50' : ''
                  }`}
                  data-testid={`notification-${notification.notification_id}`}
                >
                  <div className="flex gap-3">
                    {/* Unread indicator */}
                    <div className="flex-shrink-0 pt-1">
                      {!notification.read ? (
                        <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                      ) : (
                        <div className="w-2 h-2"></div>
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-800">
                        <span className="font-medium">{notification.from_user_name}</span>
                        {" "}{getActivityLabel(notification.activity_type)}{" "}
                        <span className="font-medium text-indigo-600">{notification.lead_name}</span>
                      </p>
                      {notification.message && (
                        <p className="text-xs text-slate-500 mt-1 truncate">
                          "{notification.message}"
                        </p>
                      )}
                      <p className="text-xs text-slate-400 mt-1">
                        {formatTime(notification.created_at)}
                      </p>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default NotificationBell;
